import { GetString } from 'fluent-react';
import CSSPlugin from 'gsap/CSSPlugin';
import TweenLite from 'gsap/TweenLite';
import max from 'lodash-es/max';
import min from 'lodash-es/min';
import React, { MutableRefObject, useRef } from 'react';
import styled from 'styled-components';
import {
  getCardinalSplinePathGenerator, getLinearScale,
} from '../../NonConstantUtils';
import { IXYPair, millisecondsPerSeconds } from '../../Utils';
import usePropsChangeRateLimiter, {
  PropsChangeHandlerInput,
} from '../sectionNav/usePropsChangeRateLimiter';
import { primaryColor1 } from '../Utils';
import { formatGDPPerCapita } from './Widget';

// Need to do this so that `CSSPlugin` is not dropped by the minifier:
/* tslint:disable-next-line:no-unused-expression */
CSSPlugin;

//#region Styling
const Root = styled.div`
  position: relative;
`;
const SVG = styled.svg`
  stroke: ${primaryColor1};
  fill: none;
  stroke-width: 2;
`;
const ProjectionLine = styled.line`
  stroke-dasharray: 2 2;
`;
const labelFontSize = 0.8 * 16; // in px
const YearBase = styled.div`
  position: absolute;
  font-size: ${labelFontSize}px;
  bottom: 0;
`;
const MinYear = styled(YearBase)`
  left: 0;
  transform: translateX(-50%) translateY(50%);
`;
const MaxYear = styled(YearBase)`
  right: 0;
  transform: translateX(50%) translateY(50%);
`;

const LabelBase = styled.div`
  position: absolute;
  top: 0;
  font-size: ${labelFontSize}px;
  color: ${primaryColor1};
`;
const FirstValue = styled(LabelBase)`
  left: 0;
  transform: translateX(-50%) translateY(-120%);
`;
const LastValue = styled(LabelBase)`
  right: 0;
  transform: translateX(50%) translateY(-120%);
`;
//#endregion

const generatePath = getCardinalSplinePathGenerator();

export interface TimeSeriesDatum {
  year: number;
  value: number;
}

export enum Unit {
  dollars,
  ranking,
}

interface Props {
  timeSeries: TimeSeriesDatum[];
  width: number;
  height: number;
  getFluentString: GetString;
  // in milliseconds
  transitionDuration: number;
  unit: Unit;
}

interface PropsChangeHandlerExtraInputs {
  pathElRef: MutableRefObject<SVGPathElement | null>;
  transitionDuration: number;
}

const havePointsChanged = (points1: IXYPair[], points2: IXYPair[]): boolean => {
  const points1Length = points1.length;
  const points2Length = points2.length;
  if (points1Length !== points2Length) {
    return true;
  } else {
    for (let i = 0; i < points1Length; i += 1) {
      const point1 = points1[i];
      const point2 = points2[i];
      if (point1.x !== point2.x || point1.y !== point2.y) {
        return true;
      }
    }
    return false;
  }
};

const performPropsChange =
  (input: PropsChangeHandlerInput<PropsChangeHandlerExtraInputs, IXYPair[]>) => {

  const {
    prevValue, nextValue, done,
    extraInputs: {
      pathElRef: {current: pathEl},
      transitionDuration,
    },
  } = input;

  if (pathEl !== null) {
    const nextPoints = nextValue;
    const prevPoints = (prevValue === undefined) ? nextPoints : prevValue;
    if (havePointsChanged(prevPoints, nextPoints) || prevValue === undefined) {
      const pathSring = generatePath(nextPoints)!;
      pathEl.style.opacity = '0';
      pathEl.setAttribute('d', pathSring);
      const pathLength = pathEl.getTotalLength();
      TweenLite.fromTo(
        pathEl,
        transitionDuration / millisecondsPerSeconds,
        {
          css: {
            'stroke-dasharray': `${pathLength} ${pathLength}`,
            'stroke-dashoffset': pathLength,
            'opacity': 1,
          },
        },
        {
          css: {
            'stroke-dashoffset': 0,
          },
          onComplete: done,
        },
      );
    } else {
      done();
    }
  } else {
    done();
  }

};

const Sparkline = (props: Props) => {
  const pathElRef = useRef<SVGPathElement | null>(null);
  const {width, height: sparklineHeight, timeSeries, transitionDuration, unit, getFluentString} = props;

  // Leave some white space at top so that if the spark line overshoots, it's not cut off by
  // the SVG's boundary:
  const topBuffer = Math.ceil(sparklineHeight * .1);

  const bottomExtensionHeight = Math.ceil(sparklineHeight * .2);

  const totalHeight = sparklineHeight + bottomExtensionHeight + topBuffer;
  const style = {width: `${width}px`, height: `${totalHeight}px`};
  const allYears = timeSeries.map(({year}) => year);
  const allValues = timeSeries.map(({value}) => value);
  const minYear = min(allYears)!;
  const maxYear = max(allYears)!;
  const { value: firstValue } = timeSeries.filter(({year}) => year === minYear)[0];
  const { value: lastValue } = timeSeries.filter(({year}) => year === maxYear)[0];
  const xScale = getLinearScale(
    [minYear, maxYear],
    [0, width],
  );
  const yScale = getLinearScale(
    [min(allValues)!, max(allValues)!],
    [sparklineHeight + topBuffer, topBuffer],
  );
  const points: IXYPair[] = timeSeries.map(({year, value}) => ({
    x: xScale(year),
    y: yScale(value),
  }));

  const extraInputs: PropsChangeHandlerExtraInputs = {
    pathElRef, transitionDuration,
  };
  usePropsChangeRateLimiter({
    value: points,
    getExtraInputToPropsChangeHandler: () => extraInputs,
    performPropsChange,
  });
  const leftProjectionLine = (
    <ProjectionLine
      x1={0} y1={0}
      x2={0} y2={sparklineHeight + topBuffer}
    />
  );
  const rightProjectionLine = (
    <ProjectionLine
      x1={width} y1={0}
      x2={width} y2={sparklineHeight + topBuffer}
    />
  );
  const firstValueText = (unit === Unit.dollars) ? formatGDPPerCapita(firstValue)
    : getFluentString('ordinal-rank-with-number', {rank: Math.abs(firstValue)});
  const lastValueText = (unit === Unit.dollars) ? formatGDPPerCapita(lastValue)
    : getFluentString('ordinal-rank-with-number', {rank: Math.abs(lastValue)});
  return (
    <Root style={style}>
      <SVG width={width} height={totalHeight}>
        {leftProjectionLine}
        {rightProjectionLine}
        <path ref={pathElRef}/>
      </SVG>
      <MinYear>{minYear}</MinYear>
      <MaxYear>{maxYear}</MaxYear>
      <FirstValue>{firstValueText}</FirstValue>
      <LastValue>{lastValueText}</LastValue>
    </Root>
  );
};

export default Sparkline;
